import { CanvasTexture, LinearMipmapLinearFilter, RepeatWrapping } from 'three';
import { createSeededRandom } from './random';

/**
 * Procedural, tileable water normal map.
 *
 * Generated from seeded value-noise fBm so no image request is issued and two
 * builds with the same seed produce identical pixel data. The lattice periods
 * wrap, so the texture tiles seamlessly across the ocean plane.
 */

function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}

function lattice(period: number, random: () => number): Float32Array {
  const values = new Float32Array(period * period);
  for (let i = 0; i < values.length; i += 1) values[i] = random();
  return values;
}

function sampleLattice(values: Float32Array, period: number, x: number, y: number): number {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const fx = smoothstep(x - x0);
  const fy = smoothstep(y - y0);
  const wrap = (v: number) => ((v % period) + period) % period;

  const x0w = wrap(x0);
  const x1w = wrap(x0 + 1);
  const y0w = wrap(y0);
  const y1w = wrap(y0 + 1);

  const v00 = values[y0w * period + x0w];
  const v10 = values[y0w * period + x1w];
  const v01 = values[y1w * period + x0w];
  const v11 = values[y1w * period + x1w];

  const top = v00 + (v10 - v00) * fx;
  const bottom = v01 + (v11 - v01) * fx;
  return top + (bottom - top) * fy;
}

/** Tileable fBm height field in roughly [0, 1]. */
export function buildHeightField(size: number, seed: number, octaves = 6): Float32Array {
  const random = createSeededRandom(seed);
  const layers = Math.max(1, octaves);
  // Non-harmonic periods keep the coarse octaves from landing on a visible
  // lattice grid once the texture is tiled across the ocean plane.
  const PERIODS = [3, 5, 8, 13, 21, 34, 55, 89, 144];
  const lattices: Float32Array[] = [];
  const periods: number[] = [];
  for (let octave = 0; octave < layers; octave += 1) {
    const period = PERIODS[Math.min(octave, PERIODS.length - 1)] ?? 4;
    lattices.push(lattice(period, random));
    periods.push(period);
  }

  const height = new Float32Array(size * size);
  let amplitude = 1;
  let total = 0;
  for (let octave = 0; octave < layers; octave += 1) total += amplitude / (octave + 1);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      let value = 0;
      amplitude = 1;
      for (let octave = 0; octave < layers; octave += 1) {
        const period = periods[octave];
        const u = (x / size) * period;
        const v = (y / size) * period;
        value += amplitude * sampleLattice(lattices[octave], period, u, v);
        amplitude *= 0.5;
      }
      height[y * size + x] = value / total;
    }
  }
  return height;
}

/**
 * Water-specific fine-detail height field.
 *
 * The water shader samples the same normal map at four very different scales,
 * so any coarse octave (period 3/5/8) becomes either a repeated blob in the
 * near layers or a broad stripe in the far layers. Drop those coarse octaves
 * and keep only mid/high frequencies, weighted so each contributes evenly.
 */
function buildWaterHeightField(size: number, seed: number): Float32Array {
  const random = createSeededRandom(seed);
  const PERIODS = [13, 21, 34, 55, 89, 144];
  const lattices = PERIODS.map((period) => lattice(period, random));
  const weights = PERIODS.map((_, index) => 1 / (index + 1));
  let total = 0;
  for (const weight of weights) total += weight;

  const height = new Float32Array(size * size);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      let value = 0;
      for (let octave = 0; octave < PERIODS.length; octave += 1) {
        const period = PERIODS[octave];
        const u = (x / size) * period;
        const v = (y / size) * period;
        value += weights[octave] * sampleLattice(lattices[octave], period, u, v);
      }
      height[y * size + x] = value / total;
    }
  }
  return height;
}

/**
 * Convert a tileable height field into an RGBA normal map (x→R, y→G, z→B).
 * `strength` scales the slope before normalisation.
 */
export function heightFieldToNormalPixels(
  height: Float32Array,
  size: number,
  strength = 2.4,
): Uint8ClampedArray {
  const pixels = new Uint8ClampedArray(size * size * 4);
  const at = (x: number, y: number) => height[(((y % size) + size) % size) * size + (((x % size) + size) % size)];

  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const dx = (at(x + 1, y) - at(x - 1, y)) * strength;
      const dy = (at(x, y + 1) - at(x, y - 1)) * strength;
      const nx = -dx;
      const ny = -dy;
      const nz = 1;
      const length = Math.hypot(nx, ny, nz) || 1;
      const offset = (y * size + x) * 4;
      pixels[offset] = Math.round((nx / length) * 0.5 * 255 + 127.5);
      pixels[offset + 1] = Math.round((ny / length) * 0.5 * 255 + 127.5);
      pixels[offset + 2] = Math.round((nz / length) * 0.5 * 255 + 127.5);
      pixels[offset + 3] = 255;
    }
  }
  return pixels;
}

/** Build the CanvasTexture the water material samples. */
export function createWaterNormalTexture(size = 256, seed = 1337, repeat = 64): CanvasTexture {
  const pixels = heightFieldToNormalPixels(buildWaterHeightField(size, seed), size, 2.8);
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Could not create water normal-map context.');
  const image = context.createImageData(size, size);
  image.data.set(pixels);
  context.putImageData(image, 0, 0);

  const texture = new CanvasTexture(canvas);
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  texture.repeat.set(repeat, repeat);
  // Mipmapped trilinear sampling keeps the near/detail layers from moiré-ing
  // toward the horizon, and anisotropy preserves the grazing-angle ripples.
  texture.minFilter = LinearMipmapLinearFilter;
  texture.generateMipmaps = true;
  texture.anisotropy = 8;
  texture.needsUpdate = true;
  return texture;
}
